import type { IndicatorTemplateData, Timeframe } from '@/types';

export interface SharedChart {
  symbol: string;
  timeframe: Timeframe;
  template: IndicatorTemplateData;
}

// /api/share lives in the Pages Functions app (same origin as the SPA), not the
// BFF worker that services/api.ts talks to — hence plain relative fetches.

async function errorMessage(res: Response): Promise<string> {
  const body = await res.json().catch(() => null) as { error?: string } | null;
  return body?.error ?? `HTTP ${res.status}`;
}

/**
 * Stores a snapshot and returns the public link that reopens it.
 * `preview` is an optional base64 JPEG shown as the link's preview card.
 */
export async function createShareLink(
  symbol: string,
  timeframe: Timeframe,
  template: IndicatorTemplateData,
  preview?: string | null,
): Promise<string> {
  const res = await fetch('/api/share', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ symbol, timeframe, template, ...(preview ? { preview } : {}) }),
  });
  if (!res.ok) throw new Error(await errorMessage(res));
  const { id } = await res.json() as { id: string };
  return `${window.location.origin}/chart/${encodeURIComponent(symbol)}?s=${id}`;
}

/** Returns null when the link doesn't exist (mistyped or never created). */
export async function fetchSharedChart(id: string): Promise<SharedChart | null> {
  const res = await fetch(`/api/share/${encodeURIComponent(id)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(await errorMessage(res));
  return res.json() as Promise<SharedChart>;
}
